import React, { useEffect, useMemo, useRef, useState } from "react";
import { FaBolt, FaCrown, FaGamepad, FaRedoAlt, FaTrophy, FaUser } from "react-icons/fa";
import { getGameRoomOverview, getMyMinesweeperDifficultyScores, resetPersonalGameSeason } from "../../../services/games/gameService";
import "./GameRoomScoreboard.css";

const GAME_LABELS = {
  "meteor-smash": "Meteor Smash",
  minesweeper: "Minesweeper",
  chess: "Chess",
  blackjack: "Blackjack",
  "block-drop": "Block Drop",
  "pixel-wings": "Pixel Wings",
  pinball: "Pinball",
};

function formatLeaderboardValue(row) {
  const value = Number(row.value || 0);
  if (row.category.endsWith("_fastest")) {
    return `${(value / 1000).toFixed(value < 100000 ? 1 : 0)}s`;
  }
  if (row.category === "blackjack_net") {
    const sign = value > 0 ? "+" : "";
    return `${sign}${value.toLocaleString()} chips`;
  }
  if (row.category === "chess_wins") {
    return `${value.toLocaleString()} wins`;
  }
  return value.toLocaleString();
}

function formatPersonalPrimary(row) {
  if (!row) return "—";
  const gameKey = row.game_key;

  if (gameKey === "minesweeper") {
    if (!row.lifetime_best_duration_ms) return "No win yet";
    return `${(Number(row.lifetime_best_duration_ms) / 1000).toFixed(1)}s best`;
  }

  if (gameKey === "chess") {
    return `${row.lifetime_wins || 0}-${row.lifetime_losses || 0}-${row.lifetime_draws || 0}`;
  }

  if (gameKey === "blackjack") {
    const net = Number(row.lifetime_net || 0);
    return `${net > 0 ? "+" : ""}${net.toLocaleString()} chips`;
  }

  return Number(row.lifetime_high_score || 0).toLocaleString();
}

export default function GameRoomScoreboard({ currentUser }) {
  const [leaderboards, setLeaderboards] = useState([]);
  const [personal, setPersonal] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [resetting, setResetting] = useState("");
  const [selectedCategory, setSelectedCategory] = useState(null);
  const closeButtonRef = useRef(null);
  const [minesweeperDetails, setMinesweeperDetails] = useState([]);
  const [minesweeperError, setMinesweeperError] = useState(false);
  const [manualPause, setManualPause] = useState({ leaders: false, personal: false });
  const liveRef = useRef(true);

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const data = await getGameRoomOverview();
      setLeaderboards(data.leaderboards || []);
      setPersonal(data.personal || []);
      if (currentUser?.id) {
        try {
          const details = await getMyMinesweeperDifficultyScores();
          if (liveRef.current) { setMinesweeperDetails(details); setMinesweeperError(false); }
        } catch (detailError) {
          console.warn("Minesweeper difficulty breakdown unavailable:", detailError);
          if (liveRef.current) { setMinesweeperDetails([]); setMinesweeperError(true); }
        }
      } else {
        setMinesweeperDetails([]);
      }
    } catch (err) {
      console.error("Game Room scoreboard failed to load:", err);
      setError("Scoreboards are unavailable until the Game Room database migration is installed.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    liveRef.current = true;
    load();
    return () => { liveRef.current = false; };
  }, [currentUser?.id]);

  useEffect(() => {
    if (!selectedCategory) return;
    const onEscape = (event) => { if (event.key === "Escape") setSelectedCategory(null); };
    document.addEventListener("keydown", onEscape);
    closeButtonRef.current?.focus();
    return () => document.removeEventListener("keydown", onEscape);
  }, [selectedCategory]);

  const champions = useMemo(() => {
    const seen = new Set();
    return leaderboards.filter((row) => {
      const key = `${row.game_key}:${row.category}`;
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
  }, [leaderboards]);

  const categoryLabel = (row) => row.game_key === "minesweeper"
    ? `Minesweeper · ${row.category.split("_")[0].replace(/^./, (c) => c.toUpperCase())}`
    : GAME_LABELS[row.game_key] || row.game_key;
  const activeLeaders = selectedCategory
    ? leaderboards.filter((row) => `${row.game_key}:${row.category}` === selectedCategory)
    : [];

  const handleReset = async (gameKey) => {
    if (!currentUser?.id || resetting) return;
    const ok = window.confirm(
      `Start a new personal ${GAME_LABELS[gameKey] || gameKey} season? Lifetime records will stay intact.`
    );
    if (!ok) return;

    setResetting(gameKey);
    try {
      await resetPersonalGameSeason(gameKey);
      await load();
    } catch (err) {
      console.error("Failed to reset game season:", err);
      setError(err.message || "Could not reset this personal season.");
    } finally {
      setResetting("");
    }
  };

  const difficultyNames = { beginner: "Beginner", intermediate: "Intermediate", expert: "Expert" };
  const formatTime = (ms) => ms == null ? "NO WIN YET" : `${(Number(ms) / 1000).toFixed(1)}s`;
  const personalGames = personal.filter((row) => Number(row.lifetime_games || 0) > 0);
  const minesweeperPresent = personalGames.some((row) => row.game_key === "minesweeper");

  const leaderItems = champions.map((row) => ({ row, key: `${row.game_key}:${row.category}` }));
  if (!champions.some((row) => row.game_key === "minesweeper" && row.category === "expert_fastest")) {
    leaderItems.push({ key: "expert-pending", pending: true });
  }

  const leaderContent = (clone = false) => leaderItems.map(({ row, key, pending }) => pending ? (
    <div className="game-room-leader-row game-room-leader-row--pending" key={`${key}-${clone}`}>
      <span className="game-room-leader-rank" aria-hidden="true"><FaTrophy /></span>
      <span className="game-room-leader-info"><strong>Minesweeper · Expert</strong><span>Awaiting first champion</span></span>
      <b>—</b>
    </div>
  ) : (
    <button className="game-room-leader-row game-room-leader-row--selectable"
      type="button" key={`${key}-${clone}`} onClick={() => setSelectedCategory(key)}
      aria-label={`View top five scores for ${categoryLabel(row)}`}
      tabIndex={clone ? -1 : 0} aria-hidden={clone ? true : undefined}>
      <span className="game-room-leader-rank" aria-hidden="true"><FaCrown /></span>
      <span className="game-room-leader-info"><strong>{categoryLabel(row)}</strong><span>{row.display_name || "Player"}</span></span>
      <b>{formatLeaderboardValue(row)}</b>
    </button>
  ));

  const personalContent = (clone = false) => personalGames.map((row) => row.game_key === "minesweeper" ? (
    <div className="game-room-personal-group" key={`mines-${clone}`}>
      <div className="game-room-personal-group__heading">
        <strong>MINESWEEPER</strong>
        {!clone && <button type="button" onClick={() => handleReset("minesweeper")}
          disabled={resetting === "minesweeper"} title="Start a new season across all three difficulties">
          <FaRedoAlt /> {resetting === "minesweeper" ? "Resetting" : "New Season"}
        </button>}
      </div>
      {minesweeperError && <span className="game-room-scoreboard__hint">Run the included difficulty statistics SQL to see separate records.</span>}
      {minesweeperDetails.length ? minesweeperDetails.map((d) => (
        <div className="game-room-personal-row game-room-personal-row--difficulty" key={d.difficulty}>
          <div><strong>{difficultyNames[d.difficulty] || d.difficulty}</strong>
            <span>{d.lifetime_wins}/{d.lifetime_games} lifetime wins · {d.season_wins}/{d.season_games} season</span>
            <span>Season best: {formatTime(d.season_best_duration_ms)}</span>
          </div>
          <b>{formatTime(d.lifetime_best_duration_ms)}</b>
        </div>
      )) : <div className="game-room-personal-row"><div><strong>Minesweeper</strong><span>{row.lifetime_games} lifetime games</span></div><b>{formatPersonalPrimary(row)}</b></div>}
    </div>
  ) : (
    <div className="game-room-personal-row" key={`${row.game_key}-${clone}`}>
      <div><strong>{GAME_LABELS[row.game_key] || row.game_key}</strong>
        <span>{row.lifetime_games || 0} lifetime · {row.season_games || 0} this season</span></div>
      <b>{formatPersonalPrimary(row)}</b>
      {!clone && <button type="button" onClick={() => handleReset(row.game_key)}
        disabled={resetting === row.game_key} title="Start a fresh personal season">
        <FaRedoAlt /> {resetting === row.game_key ? "Resetting" : "New Season"}
      </button>}
    </div>
  ));

  const credits = (type, content, canLoop) => (
    <div className={`game-room-scoreboard__viewport game-room-scoreboard__viewport--${type}${(manualPause[type] || (type === "leaders" && selectedCategory)) ? " is-paused" : ""}`}
      aria-label={`${type === "leaders" ? "All-time" : "Personal"} scores; hover or focus to pause`}>
      <div className={`game-room-scoreboard__roll${canLoop ? " game-room-scoreboard__roll--animated" : ""}`}>
        <div className="game-room-scoreboard__roll-set">{content(false)}</div>
        {canLoop && <div className="game-room-scoreboard__roll-set game-room-scoreboard__roll-set--duplicate" aria-hidden="true">{content(true)}</div>}
      </div>
    </div>
  );

  return (
    <section className="game-room-scoreboard" aria-label="Hobbli Game Room scoreboards">
      <div className="game-room-scoreboard__heading">
        <div><span>PLAYER ONE · HIGH SCORE WALL</span><h2>Arcade Scoreboards</h2></div>
        <p>Endless arcade credits · Hover or tap Pause to read your records.</p>
      </div>
      {error && <div className="game-room-scoreboard__notice">{error}</div>}
      <div className="game-room-scoreboard__grid">
        <article className="game-room-scoreboard__panel">
          <div className="game-room-scoreboard__panel-title">
            <FaCrown /><div><span>ALL-TIME</span><h3>Arcade Leaders</h3></div>
          </div>
          <div className="game-room-scoreboard__controls">
            <span>★ HALL OF FAME ★</span>
            <button type="button" onClick={() => setManualPause((p) => ({ ...p, leaders: !p.leaders }))}
              aria-pressed={manualPause.leaders}>{manualPause.leaders ? "▶ Resume" : "Ⅱ Pause"}</button>
          </div>
          {loading ? <p className="game-room-scoreboard__empty">Loading high scores…</p>
            : leaderItems.length ? credits("leaders", leaderContent, leaderItems.length > 1)
            : <p className="game-room-scoreboard__empty">The first cabinet record is waiting.</p>}
        </article>
        <article className="game-room-scoreboard__panel game-room-scoreboard__panel--personal">
          <div className="game-room-scoreboard__panel-title">
            <FaUser /><div><span>PERSONAL SCOREBOARD</span><h3>{currentUser?.display_name || "Guest Player"}</h3></div>
          </div>
          <div className="game-room-scoreboard__controls">
            <span>★ PLAYER RECORDS ★</span>
            <button type="button" onClick={() => setManualPause((p) => ({ ...p, personal: !p.personal }))}
              aria-pressed={manualPause.personal}>{manualPause.personal ? "▶ Resume" : "Ⅱ Pause"}</button>
          </div>
          {!currentUser ? <div className="game-room-scoreboard__guest"><FaGamepad /><p>Sign in to save Hobbli records and seasons.</p></div>
            : loading ? <p className="game-room-scoreboard__empty">Loading your records…</p>
            : personalGames.length ? credits("personal", personalContent, personalGames.length > 1 || minesweeperPresent)
            : <p className="game-room-scoreboard__empty">Play a game to start your personal scoreboard.</p>}
        </article>
      </div>
      {selectedCategory && (
        <div className="game-room-scoreboard__modal-backdrop" onMouseDown={(event) => {
          if (event.target === event.currentTarget) setSelectedCategory(null);
        }}>
          <div className="game-room-scoreboard__details" role="dialog" aria-modal="true"
            aria-label={`Top five leaderboard scores for ${activeLeaders[0] ? categoryLabel(activeLeaders[0]) : "selected game"}`}>
            <div className="game-room-scoreboard__rankings-heading">
              <div><span>★ HOBBLI HALL OF FAME ★</span>
                <strong>TOP 5 · {activeLeaders[0] ? categoryLabel(activeLeaders[0]) : "Records"}</strong></div>
              <button ref={closeButtonRef} type="button" onClick={() => setSelectedCategory(null)}>Close ×</button>
            </div>
            <div className="game-room-scoreboard__ranking-list">
              {activeLeaders.map((row, index) => (
                <div className="game-room-scoreboard__ranking" key={`${row.user_id}:${index}`}>
                  <span>#{index + 1}</span><strong>{row.display_name || "Player"}</strong>
                  <b>{formatLeaderboardValue(row)}</b>
                </div>
              ))}
              {activeLeaders.length < 5 && <p className="game-room-scoreboard__vacancies">
                {5 - activeLeaders.length} {5 - activeLeaders.length === 1 ? "spot" : "spots"} awaiting challengers
              </p>}
            </div>
            <p className="game-room-scoreboard__rankings-footnote">Each position represents a different player's best record.</p>
          </div>
        </div>
      )}

      <div className="game-room-scoreboard__ticker" aria-hidden="true">
        <span><FaTrophy /> HIGH SCORES</span>
        <span><FaBolt /> FASTEST TIMES</span>
        <span><FaGamepad /> BIGGEST WINNERS</span>
      </div>
    </section>
  );
}
